import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from "../index";
import {
  fetchProvider,
  fetchProviders,
  createProvider,
  updateProvider
} from "../../actions/callrecorder/providersActions";

export interface ProviderInterface {
  url: string;

  id: number,

  name: string,
  is_active: boolean,
  toll_free: boolean,
  call_verify: boolean,
  blocked_destinations: string[],
  inbound_sms_disabled_destinations: string[],
  outgoing_sms_disabled_destinations: string[],

  created_at: string,
  updated_at: string
}

export interface initialInterface {
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  count: number;

  editId: EntityId | null;
  editError: string | null;

  newParams: ProviderInterface | undefined;
  newError: string | null;
}

const initialState: initialInterface = {
  error: null,
  status: 'idle',
  count: 0,

  editId: null,
  editError: null,

  newParams: undefined,
  newError: null
};

const adaptor = createEntityAdapter<ProviderInterface>();

const slice = createSlice({
  name: 'providers',
  initialState: adaptor.getInitialState(initialState),
  reducers: {
    setNewParams: (state, action) => {
      state.newParams = action.payload;
      state.newError  = initialState.newError;
    },
    setNewError: (state, action) => {
      state.newError = action.payload;
    },
    setEditId: (state, action) => {
      state.editId    = action.payload;
      state.editError = initialState.editError;
    },
    setEditError: (state, action) => {
      state.editError = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // #index
      .addCase(fetchProviders.pending, (state, _action) => {
        state.status = 'loading'
      })
      .addCase(fetchProviders.fulfilled, (state, action) => {
        state.count = action.payload.count;
        state.entities = action.payload.providers;
        state.ids = action.payload.ids;
        state.status = 'succeeded';
      })
      .addCase(fetchProviders.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Unknown Error';
      })

      // #show
      .addCase(fetchProvider.fulfilled, (state, action) => {
        const provider = Object.values(action.payload.provider)[0];
        state.entities[provider.id] = provider;
      })

      // #create
      .addCase(createProvider.fulfilled, (state, action) => {
        const provider = Object.values(action.payload.provider)[0];
        state.entities[provider.id] = provider;
      })
      .addCase(createProvider.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.newError = error || action.error.message || 'Unknown Error';
      })

      // #update
      .addCase(updateProvider.fulfilled, (state, action) => {
        const provider = Object.values(action.payload.provider)[0];
        state.entities[provider.id] = provider;
      })
      .addCase(updateProvider.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })
  }
});

const reducer = slice.reducer;
export default reducer;

export const {
  setEditId,
  setEditError,
  setNewParams,
  setNewError
} = slice.actions;

export const {
  selectById: selectProviderById,
  selectIds: selectProviderIds,
  selectEntities: selectProviderEntities,
}  = adaptor.getSelectors<RootState>(state => state.callrecorder.providers);
